import { FC, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FarmContext } from "../../AppContent";
import { Dialog } from "../../Elements/Dialog";
import {
  resendVerificationEmail,
  resendVerificationSMS,
  verifyEmail,
  verifyPhoneNumber,
} from "../../repos/ContactRepo";
import { IContactType } from "../../types/IContactInfo";
import { OTPInput } from "../Inputs/OTPInput";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  type: IContactType;
}

export const ContactConfirmationDialog: FC<Props> = ({
  isOpen,
  onClose,
  type,
}) => {
  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);

  const { t } = useTranslation();
  const farm = useContext(FarmContext).farm!;
  const reloadFarm = useContext(FarmContext).reloadFarm;

  useEffect(() => {
    if (isOpen) {
      setCode("");
      setError(false);
    }
  }, [isOpen]);

  const onFormSubmit = () => {
    if (code.length !== 6) {
      setError(true);
      return;
    }

    setIsLoading(true);
    setError(false);

    const verifyPromise =
      type === "Email"
        ? verifyEmail(farm.uuid, {
            email: farm.contactInfo.email,
            emailVerificationCode: code,
          })
        : verifyPhoneNumber(farm.uuid, {
            phoneNumber: farm.contactInfo.phoneNumber,
            phoneNumberVerificationCode: code,
          });

    verifyPromise
      .then(() => reloadFarm())
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const onResend = () => {
    setIsLoading(true);
    setError(false);
    const resendPromise =
      type === "Email"
        ? resendVerificationEmail(farm.uuid)
        : resendVerificationSMS(farm.uuid);

    resendPromise
      .then(() => setCode(""))
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={t(`${type}ConfirmTitle`)}
      desc={t(`${type}ConfirmDesc`)}
      confirmButton={{
        onFormSubmit,
        disabled: code.length !== 6 || isLoading,
      }}
      cancelButton={{}}
      isLoading={isLoading}
    >
      <OTPInput
        value={code}
        setValue={setCode}
        error={error}
        autoFocus={isOpen}
        disabled={isLoading}
      />
      <p className="link text-center" onClick={onResend}>
        {t("ResendActivationCode")}
      </p>
    </Dialog>
  );
};
